import vtkSTLWriter from 'vtk.js/Sources/IO/Geometry/STLWriter';
import uuid from 'react-uuid';

import { Mesh } from './Mesh';
import { vtkUtil, rotateMatrix } from '../shared/vtkUtils';
import PLYWriter from '../shared/PLYWriter';
import { ExportFormats, TextureFormats } from '../shared/common';

const WMV_PREFIX = 'CSMeshViewer_';

interface ITextureItem {
  url: string;
  name: string;
}

export interface IExportItem {
  url: string;
  name: string;
  texture?: ITextureItem;
}

export interface IExportOrientation {
  title: string;
  x: number;
  y: number;
  z: number;
}

class MeshExporter {
  exportMesh(mesh: Mesh, fmt: ExportFormats, rotation?: number[]) {
    if (ExportFormats.STL === fmt) {
      return this.exportSTL(mesh, rotation);
    }
    return this.exportPLY(mesh, rotation);
  }

  exportSTL(mesh: Mesh, rotation?: number[]) {
    const writer = vtkSTLWriter.newInstance();
    writer.setInputData(vtkUtil.getPolyData(mesh?.actor));
    let matrix = mesh.matrix;
    if (rotation) {
      rotation = rotation.map((val, i) => {
        return val += mesh.rotation[i]
      })
      matrix = rotateMatrix(mesh.matrix, rotation);
    }
    writer.setTransform(matrix);
    const dataView = writer.getOutputData(0);
    const blob = new Blob([dataView.buffer]);
    const item: IExportItem = {
      url: URL.createObjectURL(blob),
      name: this.createName(mesh?.name, ExportFormats.STL),
    };
    return item;
  }

  exportPLY(mesh: Mesh, rotation?: number[]) {
    const writer = new PLYWriter();
    const txt = this.createTexture(mesh);
    let matrix = mesh.matrix
    if (rotation) {
      rotation = rotation.map((val, i) => {
        return val += mesh.rotation[i]
      })
      matrix = rotateMatrix(mesh.matrix, rotation);
    }
    const buf = writer.writeData(vtkUtil.getPolyData(mesh?.actor), matrix, txt?.name);
    const blob = new Blob([buf.buffer]);
    const item: IExportItem = {
      url: URL.createObjectURL(blob),
      name: this.createName(mesh?.name, ExportFormats.PLY),
      texture: txt,
    };
    return item;
  }

  private createName(meshName: string, fmt: ExportFormats) {
    const prefix = this.createNamePrefix(meshName);
    return prefix + '.' + fmt;
  }

  private createNamePrefix(meshName: string) {
    let name = meshName?.replaceAll(' ', '');
    if (!name || name.length <= 0) {
      name = uuid().replaceAll('-', '');
    }
    return WMV_PREFIX + name;
  }

  private createTexture(mesh: Mesh) {
    if (mesh?.mesh?.texture?.length <= 0) {
      return null;
    }
    const txt: ITextureItem = {
      url: this.createTextureUrl(mesh),
      name: this.createTextureName(mesh),
    };
    if (txt.url?.length > 0 && txt.name?.length > 0) {
      return txt;
    }
    return null;
  }

  private createTextureUrl(mesh: Mesh) {
    return mesh?.mesh?.texture;
  }

  private createTextureName(mesh: Mesh) {
    const base64Str = mesh?.mesh?.texture;
    // default JPG
    let fmt = TextureFormats.JPG;
    if (base64Str?.includes('image/png', 0)) {
      fmt = TextureFormats.PNG;
    }
    const name = this.createNamePrefix(mesh.name) + '.' + fmt;
    return name;
  }
}

const meshExporter = new MeshExporter();

export { meshExporter };
